'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Position, PRODUCT_DETAIL_VISUAL_MODAL_ID, SchemaOrgItemProp } from '@headless-workspace/config';
import { SideMenuModal, UIBrandBanner, useTailwindBreakpoint, useModalActions } from '@headless-workspace/core-ui';
import { JSONStructuredSchema, useProductDetailContext } from '@headless-workspace/domain/common/client';
import { ProductDetailValue, VipBrandsValue } from '@headless-workspace/domain/common/server';
import { GlowBreadcrumb, PropsWithAriaLabel, PropsWithFontFamily, PropsWithLocale } from '@headless-workspace/glow-ds';
import { Component, Image } from '@headless-workspace/typings';
import { ArrayHelpers } from '@headless-workspace/utils';
import {
    ProductDetailInformation,
    ProductDetailVisual,
    ProductDetailVisualModal,
    ProductDetailVisualWithoutMedia,
} from '../productDetail';
import { ProductInformationLoader } from './information/loader/ProductInformationLoader';

type ProductDetailContainerSectionProps = PropsWithAriaLabel &
    PropsWithFontFamily &
    PropsWithLocale & {
        product: ProductDetailValue;
        vipBrands: VipBrandsValue;
        redDotText?: string;
    };

export const ProductDetailContainerSection: Component<ProductDetailContainerSectionProps> = ({
    product,
    locale,
    vipBrands,
    fontFamily,
    redDotText,
}) => {
    const t = useTranslations('Common.breadcrumb');
    const isMobile = useTailwindBreakpoint('tablet', false, true);
    const { productDetails, isLoading: productVariantLoading } = useProductDetailContext();
    const [highlightedImage, setHighlightedImage] = useState<Image | undefined>(undefined);

    const handleVariantHover = (variantId: string) => {
        const highlightedImage = product.variants?.find((variant) => variant.id === variantId)?.image;
        setHighlightedImage(highlightedImage);
    };

    const selectedProduct = productDetails ?? product;

    const { addAndOpen: addAndOpenModal } = useModalActions();

    const breadcrumbMetadata = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: product.breadcrumb.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            '@id': item.id,
            name: item.label,
            item: item.url,
        })),
    };

    const onProductVisualClickOpenModal = useCallback(
        (index: number) => {
            const ProductVisualModal = <ProductDetailVisualModal medias={selectedProduct.medias} activeIndex={index} />;
            const productDetailModalClassName = 'flex flex-col w-full overflow-y-scroll bg-background-l0';
            addAndOpenModal(
                PRODUCT_DETAIL_VISUAL_MODAL_ID,
                SideMenuModal({
                    side: Position.None,
                    map: { default: ProductVisualModal },
                    className: productDetailModalClassName,
                    removeOnClose: true,
                }),
            );
        },
        [addAndOpenModal, selectedProduct.medias],
    );

    const onProductDetailVisualClick = useCallback(
        (index: number) => {
            onProductVisualClickOpenModal(index);
        },
        [onProductVisualClickOpenModal],
    );

    const mainImage = product.medias.find((media) => media.type === 'IMAGE');

    return (
        <>
            {productVariantLoading && <ProductInformationLoader />}
            <section id={'product-detail-container'} className={'flex flex-col max-w-full'}>
                {product.hasProductDetailLogoBanner && (
                    <UIBrandBanner
                        logo={product.brand.logo}
                        redirectionURL={product.brand.url}
                        hasWhiteLogoBackground={product.hasWhiteLogoBackground}
                        logoPosition={product.brandLogoPosition}
                    />
                )}
                <div className={'px-1 py-0.75 desktop:px-1.5'}>
                    <JSONStructuredSchema content={breadcrumbMetadata} />
                    <GlowBreadcrumb
                        breadcrumb={product.breadcrumb}
                        ariaLabel={t('ariaLabel.name')}
                        seeAllAriaLabel={t('ariaLabel.seeAll')}
                        isMobile={isMobile}
                    />
                </div>
                <article
                    className={
                        'flex flex-col desktopS:flex-row gap-1.5 tablet:gap-3 desktopS:gap-1.5 desktop:gap-5 desktop:px-1.5 py-0.75 desktop:py-0'
                    }
                >
                    {mainImage && <meta itemProp={SchemaOrgItemProp.Image} content={mainImage.url} />}
                    {ArrayHelpers.isNotEmpty(selectedProduct.medias) ? (
                        <ProductDetailVisual
                            onProductDetailVisualClick={onProductDetailVisualClick}
                            medias={selectedProduct.medias}
                            flags={product.flags}
                            socialProof={product.socialProof}
                            highlightedImage={highlightedImage}
                        />
                    ) : (
                        <ProductDetailVisualWithoutMedia />
                    )}
                    <ProductDetailInformation
                        product={product}
                        locale={locale}
                        vipBrands={vipBrands}
                        fontFamily={fontFamily}
                        redDotText={redDotText}
                        onVariantHovered={handleVariantHover}
                    />
                </article>
            </section>
        </>
    );
};
